import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from "react-dom";
import { AnimatePresence } from "framer-motion";
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Copy, Share2, Gift, Users, CheckCircle } from 'lucide-react';
import { Keyboard } from '@capacitor/keyboard';
import { useAppSettings } from '../components/providers/AppSettingsProvider';
import { getPageBackgroundGradient } from '../config/theme';
import { getUserProfile, redeemReferralCode, type UserProfile } from '../services/userService';
import { toast } from 'sonner';

const ReferralPage: React.FC = () => {
    const { theme, t } = useAppSettings();
    const isDark = theme === 'dark';
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [redeemCode, setRedeemCode] = useState('');
    const [redeeming, setRedeeming] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    // Explicit reference to the input field
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        loadProfile();

        // Add keyboard listeners for iOS/Android
        const onKeyboardShow = () => {
            console.log("Keyboard showed event received.");
            // Check if our input is the active one, or just force scroll it if it's the only one relevant
            if (inputRef.current) {
                console.log("Input ref found, attempting scroll...");
                // Use setTimeout to allow layout to settle
                setTimeout(() => {
                    inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    console.log("Scroll command executed.");
                }, 100);
            } else {
                console.log("Input ref NOT found.");
            }
        };

        let keyboardListener: any; // Type 'any' to avoid PluginListenerHandle type issues without importing it

        Keyboard.addListener('keyboardDidShow', onKeyboardShow).then(handle => {
            keyboardListener = handle;
        }).catch(err => console.error('Keyboard listener error:', err));

        return () => {
            if (keyboardListener) {
                keyboardListener.remove();
            }
        };
    }, []);

    const loadProfile = async () => {
        try {
            const p = await getUserProfile();
            setProfile(p);
        } catch (e) {
            console.error(e);
            toast.error("Failed to load profile");
        } finally {
            setLoading(false);
        }
    };

    const handleCopyCode = () => {
        if (profile?.referral_code) {
            navigator.clipboard.writeText(profile.referral_code);
            toast.success(t('referral.copySuccess'));
        }
    };

    const handleShare = async () => {
        if (!profile?.referral_code) return;

        // TODO: I18n for share text
        // Combine text and URL to ensure the code is visible in apps like WeChat that prioritize Link Cards over text.
        // We'll try to share as text-only if possible, or reliance on the platform's text parser.
        const shareUrl = 'https://apps.apple.com/us/app/call-lumi/id6753812745';
        
        const shareMessage = t('referral.share.message', {
            code: profile.referral_code,
            minutes: profile.referral_reward_minutes || 30,
            url: shareUrl
        });

        const shareData = {
            title: t('referral.share.title'),
            text: shareMessage,
            // url: shareUrl // Commented out to force "Text Share" behavior which usually preserves the message
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                handleCopyCode();
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleRedeem = async () => {
        if (!redeemCode.trim()) return;

        setRedeeming(true);
        try {
            await redeemReferralCode(redeemCode);
            // toast.success(`Code redeemed successfully! ${profile?.referral_reward_minutes || 30} mins added.`);
            setShowSuccessModal(true);
            setRedeemCode('');
            loadProfile(); // Refresh to update balance/status
        } catch (e: any) {
            toast.error(e.message || "Redemption failed");
        } finally {
            setRedeeming(false);
        }
    };

    const containerAnimation = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemAnimation = {
        hidden: { opacity: 0, y: 20 },
        show: { opacity: 1, y: 0 }
    };

    return (
        <div className={`fixed inset-0 overflow-hidden ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
            <div className={`absolute inset-0 bg-gradient-to-b ${getPageBackgroundGradient(theme)}`} />

            {/* Header */}
            <div className="absolute top-0 left-0 right-0 z-20 px-4 pt-12 pb-4 flex items-center">
                <button
                    onClick={() => navigate(-1)}
                    className={`p-2 rounded-full backdrop-blur-md ${isDark ? 'bg-white/10 text-white' : 'bg-white/60 text-gray-800'}`}
                >
                    <ChevronLeft className="w-6 h-6" />
                </button>
                <h1 className={`ml-4 text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    {t('referral.title')}
                </h1>
            </div>

            {/* Content */}
            <div className="relative z-10 h-full overflow-y-auto pt-28 pb-24 px-6">
                {loading ? (
                    <div className="flex justify-center pt-20">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <motion.div
                        variants={containerAnimation}
                        initial="hidden"
                        animate="show"
                        className="max-w-md mx-auto space-y-6"
                    >
                        {/* Reward Banner */}
                        <motion.div variants={itemAnimation} className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white shadow-xl">
                            <div className="relative z-10 flex flex-col items-center text-center">
                                <Gift className="w-12 h-12 mb-3 text-yellow-300" />
                                <h2 className="text-2xl font-bold mb-2">{t('referral.reward.title', { minutes: profile?.referral_reward_minutes || 30 })}</h2>
                                <p className="text-white/90 text-sm">
                                    {t('referral.reward.subtitle', { minutes: profile?.referral_reward_minutes || 30 })}
                                </p>
                            </div>
                        </motion.div>

                        {/* My Code Section */}
                        <motion.div variants={itemAnimation} className={`rounded-2xl p-6 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white shadow-sm'}`}>
                            <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-white/70' : 'text-gray-500'}`}>
                                {t('referral.code.title')}
                            </h3>

                            <div className="flex items-center gap-3 mb-6">
                                <div className={`flex-1 p-4 rounded-xl text-center text-xl font-mono font-bold tracking-wider border border-dashed ${isDark ? 'bg-blue-500/10 border-blue-400/30 text-blue-300' : 'bg-blue-50 border-blue-200 text-blue-600'
                                    }`}>
                                    {profile?.referral_code || 'LOADING...'}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={handleCopyCode}
                                    className={`flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-colors ${isDark ? 'bg-white/10 hover:bg-white/15 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                                        }`}
                                >
                                    <Copy className="w-4 h-4" />
                                    {t('referral.copy')}
                                </button>
                                <button
                                    onClick={handleShare}
                                    className="flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-colors bg-blue-500 hover:bg-blue-600 text-white shadow-lg shadow-blue-500/20"
                                >
                                    <Share2 className="w-4 h-4" />
                                    {t('referral.share')}
                                </button>
                            </div>

                            {/* Progress */}
                            <div className="mt-6 pt-6 border-t border-dashed border-gray-200 dark:border-white/10">
                                <div className="flex justify-between items-end mb-2">
                                    <span className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>{t('referral.stats.monthly')}</span>
                                    <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                                        {profile?.referrals_count_current_month || 0}
                                        <span className={`text-sm font-normal ml-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                                            / {profile?.referral_monthly_limit || 5}
                                        </span>
                                    </span>
                                </div>
                                <div className="h-2 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                        style={{ width: `${Math.min(((profile?.referrals_count_current_month || 0) / (profile?.referral_monthly_limit || 5)) * 100, 100)}%` }}
                                    />
                                </div>
                                <p className={`text-xs mt-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                                    {profile && profile.referrals_count_current_month >= (profile.referral_monthly_limit || 5)
                                        ? t('referral.stats.limitReached')
                                        : t('referral.stats.resets')}
                                </p>
                            </div>
                        </motion.div>

                        {/* Redeem Section */}
                        <motion.div variants={itemAnimation} className={`rounded-2xl p-6 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white shadow-sm'}`}>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 rounded-full bg-orange-100 dark:bg-orange-500/20 text-orange-500">
                                    <Users className="w-5 h-5" />
                                </div>
                                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                                    {t('referral.redeem.title')}
                                </h3>
                            </div>

                            {!profile?.can_redeem_referral ? (
                                <div className={`p-4 rounded-xl flex items-start gap-3 ${isDark ? 'bg-green-500/10 text-green-300' : 'bg-green-50 text-green-700'
                                    }`}>
                                    <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm">{t('referral.redeem.successRecord')}</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        placeholder={t('referral.redeem.placeholder')}
                                        value={redeemCode}
                                        onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                                        className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-blue-500 outline-none transition-all ${isDark
                                            ? 'bg-black/20 border-white/10 text-white placeholder-white/30'
                                            : 'bg-gray-50 border-gray-200 text-gray-800'
                                            }`}
                                    />
                                    <button
                                        onClick={handleRedeem}
                                        disabled={!redeemCode.trim() || redeeming}
                                        className={`w-full py-3 rounded-xl font-medium transition-all ${!redeemCode.trim() || redeeming
                                            ? 'bg-gray-300 dark:bg-white/10 text-gray-500 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white shadow-lg shadow-orange-500/20'
                                            }`}
                                    >
                                        {redeeming ? t('referral.redeem.processing') : t('referral.redeem.button')}
                                    </button>
                                </div>
                            )}
                        </motion.div>

                        <div className="h-[25vh]"></div>
                    </motion.div>
                )}
            </div>


            {/* Success Modal */}
            <RedeemSuccessModal
                show={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                rewardMinutes={profile?.referral_reward_minutes || 30}
            />
        </div >
    );
};

interface RedeemSuccessModalProps {
    show: boolean;
    onClose: () => void;
    rewardMinutes: number;
}

function RedeemSuccessModal({ show, onClose, rewardMinutes }: RedeemSuccessModalProps) {
    const { theme, t } = useAppSettings();
    const isDark = theme === 'dark';

    if (typeof document === 'undefined') return null;

    return createPortal(
        <AnimatePresence>
            {show && (
                <motion.div
                    className="fixed inset-0 z-[9999]"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
                    <motion.div
                        className="absolute inset-x-0 bottom-0 backdrop-blur-2xl border-t border-white/20 rounded-t-[32px] px-6 pt-6 pb-8"
                        style={{
                            background: isDark
                                ? 'linear-gradient(to bottom, rgba(60, 136, 238, 0.95), rgba(107, 187, 252, 0.95))'
                                : 'linear-gradient(to bottom, rgba(200, 230, 255, 0.95), rgba(230, 245, 255, 0.95))',
                            zIndex: 9999,
                            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 2rem)',
                        }}
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', stiffness: 260, damping: 25 }}
                    >
                        <div className={`mx-auto w-12 h-1.5 rounded-full mb-6 ${isDark ? 'bg-white/30' : 'bg-white/50'}`} />
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-4 text-3xl">
                                🎉
                            </div>
                            <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                                {t('referral.successModal.title')}
                            </h2>
                            <div className={`text-base leading-relaxed ${isDark ? 'text-white/90' : 'text-gray-700'}`}>
                                {t('referral.successModal.subtitle', { minutes: rewardMinutes })}
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className={`w-full py-3.5 px-6 rounded-xl text-base font-bold shadow-lg transition-transform active:scale-95 ${isDark
                                ? 'bg-white text-blue-600'
                                : 'bg-blue-600 text-white'
                                }`}
                        >
                            {t('referral.successModal.button')}
                        </button>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default ReferralPage;
